import React from 'react';
import { MapPinIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { MapPinIcon as MapPinOutlineIcon } from '@heroicons/react/24/outline';

const FixedLocationSelector = ({
  locations = [],
  selectedLocation,
  onSelect,
  label = "Choose Location",
  showSearchOption = true,
  onSearchClick,
}) => {
  const handleLocationSelect = (location) => {
    onSelect(location);
  };

  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick();
    }
  };

  if (!locations || locations.length === 0) {
    return (
      <div className="space-y-3">
        <label className="text-sm font-medium leading-6 text-ink-900">
          {label}
        </label>
        <div className="text-center py-6 text-ink-500">
          <MapPinOutlineIcon className="mx-auto h-12 w-12 text-ink-400" />
          <p className="mt-2 text-sm">No saved business locations available</p>
          {!showSearchOption && (
            <p className="mt-1 text-xs text-ink-400">
              Please configure your business locations in the Availability settings.
            </p>
          )}
          {showSearchOption && (
            <button
              type="button"
              onClick={handleSearchClick}
              className="mt-2 text-sm text-accent-600 hover:text-accent-500"
            >
              Search for an address
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label id={`${label}-label`} className="text-sm font-medium leading-6 text-ink-900">
        {label}
      </label>

      <div className="grid gap-3" role="radiogroup" aria-labelledby={`${label}-label`}>
        {locations.map((location) => {
          const isSelected = selectedLocation?.id === location.id;

          return (
            <div
              key={location.id}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 ${
                isSelected
                  ? 'border-accent-500 bg-accent-50 shadow-hairline'
                  : 'border-ink-200 bg-white shadow-hairline hover:border-ink-300 hover:bg-cream-50'
              }`}
              onClick={() => handleLocationSelect(location)}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLocationSelect(location);
                }
              }}
            >
              <div className="flex items-start space-x-3">
                <div className={`flex-shrink-0 transition-colors duration-200 ${
                  isSelected ? 'text-accent-500' : 'text-ink-400'
                }`}>
                  <MapPinIcon className="h-5 w-5" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className={`text-sm font-medium break-words transition-colors duration-200 ${
                      isSelected ? 'text-accent-700' : 'text-ink-900'
                    }`} style={{ overflowWrap: 'anywhere', lineHeight: '1.4' }}>
                      {location.name}
                    </p>
                    {isSelected && (
                      <CheckCircleIcon className="h-5 w-5 text-accent-500 flex-shrink-0 mt-0.5" />
                    )}
                  </div>

                  {/* Only show the address if it's different from the name */}
                  {location.address && location.address !== location.name && (
                    <p className={`text-sm break-words mt-1 transition-colors duration-200 ${
                      isSelected ? 'text-accent-600' : 'text-ink-500'
                    }`} style={{ overflowWrap: 'anywhere', lineHeight: '1.3' }}>
                      {location.address}
                    </p>
                  )}

                  {/* Show business hours if available */}
                  {location.hours && (
                    <p className={`text-xs mt-1 transition-colors duration-200 ${
                      isSelected ? 'text-accent-500' : 'text-ink-400'
                    }`}>
                      {location.hours}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showSearchOption && (
        <div className="pt-3 border-t border-ink-100">
          <button
            type="button"
            onClick={handleSearchClick}
            className="w-full mt-3 inline-flex items-center justify-center gap-x-2 px-3 py-2 text-sm text-ink-600 hover:text-accent-600 hover:bg-cream-100 rounded-pill transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2"
          >
            <MapPinOutlineIcon className="h-4 w-4" />
            <span>Search for a different address</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default FixedLocationSelector;
